import { useEffect, useState } from 'react';
import client from './api';
import { categories as fallbackCategories } from '../mock/mock';
import { useHiddenCategoriesState } from './gameCategories';

/**
 * Ana sayfadaki kategori kartları.
 *
 * Kartlar panelden yönetilir (Banner Yönetimi → Kategori Kartları). Uç henüz
 * yanıt vermeden ya da eski bir backend'e karşı çalışırken sayfanın kartsız
 * kalmaması için kodda gömülü liste yedek olarak durur; panelden bir kart
 * kaydedildiği anda yedek devre dışı kalır.
 */
const FALLBACK_GAME_CATEGORY = {
  slot: 'slot',
  canli: 'live-casino',
  crash: 'crash',
  spor: 'sports',
};

const fallbackCards = fallbackCategories.map((category, index) => ({
  id: category.id,
  card_key: category.id,
  title: category.title,
  subtitle: category.subtitle,
  cta_text: category.cta,
  href: category.href,
  image: category.img,
  game_category: FALLBACK_GAME_CATEGORY[category.id] || '',
  order: index + 1,
}));

/**
 * Kartın çizildiği cihaz (`home_categories.device`).
 *
 * `all` her ekranda görünür ve varsayılandır; `desktop` / `mobile` kartı tek
 * bir genişliğe ayırır. Aynı alanda masaüstünde bir set, telefonda başka bir
 * set durabilsin diye vardır: Betrun'da masaüstünde beş dikey kart, telefonda
 * sekiz ikon kutusu. CSS ile yapılamazdı — iki set farklı içeriktir, yalnızca
 * görünümleri değil.
 *
 * Sınır tek yerdedir ve Betrun'un kutu ızgarasının dörtlü düzene geçtiği
 * genişlikle aynıdır (betrun.css `max-width: 1024px`).
 */
export const HOME_CATEGORY_DEVICES = ['all', 'desktop', 'mobile'];
export const MOBILE_QUERY = '(max-width: 1024px)';

export const normalizeCardDevice = (value) => (
  HOME_CATEGORY_DEVICES.includes(value) ? value : 'all'
);

export const cardMatchesDevice = (card, device) => {
  const own = normalizeCardDevice(card?.device);
  return own === 'all' || !device || own === device;
};

const currentDevice = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'desktop';
  }
  return window.matchMedia(MOBILE_QUERY).matches ? 'mobile' : 'desktop';
};

/** Ekranın o anki cihaz sınıfı; genişlik değişince (döndürme) güncellenir. */
export const useViewportDevice = () => {
  const [device, setDevice] = useState(currentDevice);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const query = window.matchMedia(MOBILE_QUERY);
    const update = () => setDevice(query.matches ? 'mobile' : 'desktop');
    update();
    if (query.addEventListener) query.addEventListener('change', update);
    else if (query.addListener) query.addListener(update);
    return () => {
      if (query.removeEventListener) query.removeEventListener('change', update);
      else if (query.removeListener) query.removeListener(update);
    };
  }, []);
  return device;
};

/**
 * Kapalı kategorinin kartını eler.
 *
 * `hiddenCategories` hem yönetici tarafından kapatılan hem de kataloğu boşalan
 * kategorileri taşır (bkz. gameCategories.js). Spor bahsi ayrı bir bayrakla
 * yönetildiği için `sports` kartı ona bakar.
 */
export const visibleHomeCategories = (cards, hiddenCategories, sportsEnabled, device) => (
  cards.filter((card) => {
    // Cihaz verilmezse süzülmez (eski çağrılar); verilirse o ekrana
    // ayrılmamış kart düşer.
    if (!cardMatchesDevice(card, device)) return false;
    const bound = card.game_category || '';
    if (bound === 'sports') return sportsEnabled !== false;
    if (!bound) return true;
    return !hiddenCategories.includes(bound);
  })
);

// Son bilinen kart listesi localStorage'da durur (banner önbelleğiyle aynı
// yöntem). Eskiden ilk boyama gömülü yedek listeyle yapılıyordu: yönetici
// Crash kartını kapatmış olsa bile kart her yenilemede bir an çiziliyor,
// altındaki kartları aşağı itip yanıt gelince düşüyordu.
export const HOME_CATEGORIES_CACHE_KEY = 'app:home-categories-v1';

const MAX_CARDS = 24;

const storageOf = (storage) => (
  storage !== undefined
    ? storage
    : (typeof window !== 'undefined' ? window.localStorage : null)
);

export const readCachedHomeCategories = (storage) => {
  try {
    const raw = storageOf(storage)?.getItem(HOME_CATEGORIES_CACHE_KEY);
    const items = raw ? JSON.parse(raw) : null;
    if (!Array.isArray(items)) return null;
    return items.filter((card) => card && typeof card === 'object').slice(0, MAX_CARDS);
  } catch {
    return null;
  }
};

export const cacheHomeCategories = (items, storage) => {
  try {
    storageOf(storage)?.setItem(
      HOME_CATEGORIES_CACHE_KEY,
      JSON.stringify(items.slice(0, MAX_CARDS)),
    );
  } catch {
    // Depolama kapalıysa bir sonraki açılış yine yanıtı bekler.
  }
};

/**
 * `cards` bilinmiyorsa `null`dır: önbellek yoksa yanıt gelene kadar kart
 * çizilmez. Uç hata verirse gömülü yedek listeye düşülür.
 */
export const useHomeCategoriesState = () => {
  const [cards, setCards] = useState(() => readCachedHomeCategories());

  useEffect(() => {
    let active = true;
    client.get('/home-categories')
      .then(({ data }) => {
        const items = data?.items || [];
        // Yönetici son kartı da sildiyse yedek listeye dönmek yanlış olurdu;
        // boş yanıt "kart gösterme" demektir.
        if (!active || !Array.isArray(items)) return;
        cacheHomeCategories(items);
        setCards(items);
      })
      .catch(() => { if (active) setCards((current) => current || fallbackCards); });
    return () => { active = false; };
  }, []);

  return cards;
};

export const useHomeCategories = () => useHomeCategoriesState() || [];

/**
 * Vitrinin çizeceği kartlar. Kart listesi ya da kapalı kategoriler henüz
 * bilinmiyorsa boş döner: bilinmeden çizilen kart kapalı olabilir ve sonradan
 * düşünce altındaki içerik kayar. Önbellek dolu olduğu sürece (ilk ziyaret
 * dışında) iki bilgi de ilk boyamada hazırdır.
 */
export const useVisibleHomeCategories = (sportsEnabled) => {
  const cards = useHomeCategoriesState();
  const { hidden, known } = useHiddenCategoriesState();
  // Süzgeç bütün şemalar için buradadır: şema başına yazılsaydı biri
  // unutulduğunda "yalnızca mobil" kart masaüstünde de çizilirdi.
  const device = useViewportDevice();
  if (!cards || !known) return [];
  return visibleHomeCategories(cards, hidden, sportsEnabled, device);
};

export default useHomeCategories;
